import { useEffect, useRef } from "react";
import type { ChangeEvent } from "react";
import { useDocumentEventListener } from "../../../../hooks/useDocumentEventListener";

interface UseChoiceGroupOptions {
  options: string[];
  value: string[];
  multiple: boolean;
  onChange: (next: string[]) => void;
  onRawChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  focusedIndex?: number;
  onFocusChange?: (index: number) => void;
  keyboardNavigation: boolean;
}

interface UseChoiceGroupResult {
  /** The highlighted row, clamped to the options; undefined when no row is highlighted. */
  focused: number | undefined;
  /** Ref callbacks to attach to each option's row and input. */
  rowRef: (index: number) => (el: HTMLDivElement | null) => void;
  inputRef: (index: number) => (el: HTMLInputElement | null) => void;
  /** Handles an input's change event: reports it raw, then reports the new selection. */
  handleChange: (option: string, event: ChangeEvent<HTMLInputElement>) => void;
  /** Selects the option at `index` as if its input had been clicked. */
  clickInput: (index: number) => void;
}

/**
 * The behaviour of a ChoiceGroup: the new selection derived from a change event, the highlighted
 * row kept in view, and (when enabled) ↑/↓ to move the highlight and Space to select it.
 */
export function useChoiceGroup({
  options,
  value,
  multiple,
  onChange,
  onRawChange,
  focusedIndex,
  onFocusChange,
  keyboardNavigation,
}: UseChoiceGroupOptions): UseChoiceGroupResult {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const rows = useRef<(HTMLDivElement | null)[]>([]);
  const focused =
    focusedIndex === undefined
      ? undefined
      : Math.max(0, Math.min(options.length - 1, focusedIndex));

  useEffect(() => {
    if (focused !== undefined) rows.current[focused]?.scrollIntoView({ block: "nearest" });
  }, [focused]);

  useDocumentEventListener("keydown", (event) => {
    if (!keyboardNavigation || focused === undefined) return;
    const target = event.target as HTMLElement;
    // An input handles its own keys natively; other fields are not ours to steal from.
    if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      onFocusChange?.(Math.max(0, focused - 1));
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      onFocusChange?.(Math.min(options.length - 1, focused + 1));
    } else if (event.key === " ") {
      event.preventDefault();
      inputs.current[focused]?.click();
    }
  });

  return {
    focused,
    rowRef: (index) => (el) => {
      rows.current[index] = el;
    },
    inputRef: (index) => (el) => {
      inputs.current[index] = el;
    },
    handleChange(option, event) {
      onRawChange?.(event);
      if (!multiple) onChange([option]);
      else onChange(event.target.checked ? [...value, option] : value.filter((o) => o !== option));
    },
    clickInput(index) {
      inputs.current[index]?.click();
    },
  };
}
